import { Fragment, useState } from "react"
import { Star, ChevronRight } from "lucide-react"
import { useMediaQuery } from "@react-hookz/web"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import type { PromptCategory } from "@/api/adHocCommands"
import { useFtueT } from "./agentFtueEnglish"

type Props = {
  popular: string[]
  groups: PromptCategory[]
  onSelectPrompt: (text: string) => void
  className?: string
  /** Render the static labels in English (english-agent-ftue flag). */
  english?: boolean
  /** The demo switch is on PL: render the labels in Polish. */
  polish?: boolean
}

function mentionsDriver(text: string): boolean {
  return text.includes("#")
}

function PromptRow({
  text,
  onSelect,
  english,
  polish,
}: {
  text: string
  onSelect: (t: string) => void
  english?: boolean
  polish?: boolean
}) {
  const t = useFtueT(english ?? false, polish)
  return (
    <button
      type="button"
      onClick={() => onSelect(text)}
      className="flex w-full items-center gap-2 rounded-md border border-border bg-transparent px-3 py-2 text-left text-sm transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <span className="flex-1">{text}</span>
      {mentionsDriver(text) && (
        <Badge variant="secondary" className="shrink-0 text-[10px]">
          {t("ai.agentCapabilities.driverBadge")}
        </Badge>
      )}
    </button>
  )
}

export function AgentCapabilities({
  popular,
  groups,
  onSelectPrompt,
  className,
  english = false,
  polish = false,
}: Props) {
  const t = useFtueT(english, polish)
  const [openName, setOpenName] = useState<string | null>(null)
  // Matches the `sm:grid-cols-2` breakpoint below. Defaults to wide so the
  // panel placement is correct before matchMedia resolves.
  const isWide = useMediaQuery("(min-width: 640px)") ?? true

  if (popular.length === 0 && groups.length === 0) return null

  const cols = isWide ? 2 : 1
  const openIndex = openName ? groups.findIndex((g) => g.name === openName) : -1
  const openGroup = openIndex >= 0 ? groups[openIndex] : null
  // Insert the questions panel after the LAST card of the open card's row, so
  // the category cards keep their grid positions (Google-Images style) instead
  // of the clicked card growing in place and reflowing the grid.
  const rowLastIndex =
    openIndex >= 0
      ? Math.min(
          Math.floor(openIndex / cols) * cols + (cols - 1),
          groups.length - 1,
        )
      : -1

  return (
    <div className={cn("flex flex-col gap-5", className)}>
      {popular.length > 0 && (
        <section>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Star className="h-3.5 w-3.5" aria-hidden="true" />{" "}
            {t("ai.agentCapabilities.popularQuestions")}
          </div>
          <div className="flex flex-wrap gap-2">
            {popular.map((p) => (
              <Button
                key={p}
                type="button"
                variant="outline"
                size="sm"
                className="h-auto rounded-full px-3 py-1.5 text-sm font-normal"
                onClick={() => onSelectPrompt(p)}
              >
                {p}
              </Button>
            ))}
          </div>
        </section>
      )}

      {groups.length > 0 && (
        <section>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("ai.agentCapabilities.allAreas")}
          </div>
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-2">
            {groups.map((g, i) => {
              const open = openName === g.name
              return (
                <Fragment key={g.name}>
                  <button
                    type="button"
                    onClick={() => setOpenName(open ? null : g.name)}
                    aria-expanded={open}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg border px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      open
                        ? "border-primary/40 bg-primary/5"
                        : "border-border hover:border-primary/40 hover:bg-primary/5",
                    )}
                  >
                    <span className="text-xl leading-none">{g.icon}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold">
                        {g.title}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {g.shortDescription}
                      </span>
                    </span>
                    <ChevronRight
                      className={cn(
                        "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                        open && "rotate-90 text-primary",
                      )}
                    />
                  </button>
                  {openGroup && i === rowLastIndex && (
                    <div className="col-span-full flex flex-col gap-2 rounded-lg border border-primary/40 bg-primary/5 p-3">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <span className="text-base leading-none">
                          {openGroup.icon}
                        </span>
                        {openGroup.title}
                      </div>
                      {openGroup.prompts.map((p) => (
                        <PromptRow
                          key={p}
                          text={p}
                          onSelect={onSelectPrompt}
                          english={english}
                          polish={polish}
                        />
                      ))}
                    </div>
                  )}
                </Fragment>
              )
            })}
          </div>
        </section>
      )}
    </div>
  )
}
